<script>
import axios from 'axios';
import VueSelect from 'vue-select';
import { debounce, filter } from 'lodash';
import Pagination from '../Pagination.vue';

export default {
    components: {
        VueSelect,
        Pagination,
    },
    props: {
        userId: {
            type: String,
            required: true,
        },
        verificationTypes: {
            type: Object|Array,
            required: true,
        },
    },

    data() {
        return {
            summary: {
                pending: 0,
                in_progress: 0,
                completed: 0,
                failed: 0,
            },
            backgroundVerificationResults: {},
            filterParams: {
                requisition: null,
            },
            requisitions: [],
            totalPages: 1,
            currentPage: 1,
            statuses: [
                { title: 'In Progress' },
                { title: 'Completed' },
                { title: 'Failed' },
            ],
        }
    },

    methods: {
        async loadIfactsCheck() {
            try {
                let filterQuery = '';

                if (this.filterParams.requisition) {
                    filterQuery += `&requisition_id=${this.filterParams.requisition.id}`;
                }

                if (this.filterParams.verification_type) {
                    filterQuery += `&verification_type=${this.filterParams.verification_type.title}`;
                }

                if (this.filterParams.status) {
                    filterQuery += `&status=${this.filterParams.status.title}`;
                }

                const data = await axios.get(`/ajax/background-verification/candidate-dashboard?page=${this.currentPage}${filterQuery}`);
                this.summary = data.data.summary;
                this.backgroundVerificationResults = data.data.backgroundVerificationResults;
                this.totalPages = data.data.paginationMeta.pageCount;
                this.currentPage = data.data.paginationMeta.page;
            } catch (e) {
                console.error(e);
                alert('Unable to modify baseline. Please try again');
            }

        },
        sliceString(value) {
            return value.substring(value.lastIndexOf("/") + 1)
        },
        fetchRequisitions(search = '', loading = null) {
            if (loading) loading(true);
            this.search(`/ajax/requisitions?search=${search}&limit=1000`, loading, json => {
                this.requisitions = json.requisitions
            });
        },
        search: debounce((url, loading, mapResult) => {
            fetch(url).then(res => {
                res.json().then(mapResult);
                if (loading) loading(false);
            });
        }, 250),
        requisitionTitle(option) {
            let title = option.display_title;
            if (title === null || title.length <= 0) {
                if (option.job) {
                    title = option.job.title;
                }
            }
            return `${title} (${option.id})`;
        },
        requisitionTitleTable(option) {
            let title = option.requisition.display_title;
            if (title === null || title.length <= 0) {
                if (option.title) {
                    title = option.title;
                }
            }
            return `${title} (${option.requisition_id})`;
        },
        selectedRequisitionTitle(option) {
            let title = option.display_title;
            if (title === null || title.length <= 0) {
                if (option.job) {
                    title = option.job.title;
                }
            }
            return `${title} (${option.id})`;
        },
        handlePageChange(page) {
            this.currentPage = page;
            this.loadIfactsCheck()
        },
        updateResultStatus(id, result) {
            const formData = {
                id: id,
                result: result
            }

            axios.post(`/ajax/background-verification/update-status/${id}`, formData).then(function (response) {
                if (response.statusText !== "OK") {
                    throw new Error();
                }
            });
        },
    },

    mounted() {
        this.loadIfactsCheck();
    },

    watch: {
        filterParams: {
            handler: function () {
                this.currentPage = 1;
                this.loadIfactsCheck();
            },
            deep: true
        }
    }
}
</script>

<template>
    <div>
        <section class="mb-2">
            <div class="card">
                <div class="grid-x grid-margin-x grid-margin-y py-2 px-2">
                    <div class="cell medium-6 xlarge-4">
                        <label>Requisitions</label>
                        <vue-select
                            @search="fetchRequisitions"
                            @open="fetchRequisitions"
                            :options="requisitions"
                            v-model="filterParams.requisition"
                            :filterable="false"
                        >
                            <template slot="option" slot-scope="option">
                                {{ requisitionTitle(option) }}
                            </template>

                            <template slot="selected-option" slot-scope="option">
                                {{ selectedRequisitionTitle(option) }}
                            </template>
                        </vue-select>
                    </div>
                    <div class="cell medium-6 xlarge-2">
                        <label>Verification Type</label>
                        <vue-select label="title"
                            :options="verificationTypes"
                            v-model="filterParams.verification_type"
                        >
                            <template slot="option" slot-scope="option">
                                {{ option.title }}
                            </template>
                        </vue-select>
                    </div>
                    <div class="cell medium-6 xlarge-2">
                        <label>Status</label>
                        <vue-select label="title"
                            :options="statuses"
                            v-model="filterParams.status"
                        >
                            <template slot="option" slot-scope="option">
                                {{ option.title }}
                            </template>
                        </vue-select>
                    </div>
                </div>
            </div>
        </section>

        <section>
            <div class="card">
                <div class="grid-x grid-margin-x grid-margin-y py-2 px-2">
                    <div class="cell small-6 medium-4 xlarge-4">
                        <div class="card">
                            <div class="count-card">
                                <span class="count-number">{{ summary.in_progress }}</span>
                                <span class="count-name">In Progress</span>
                            </div>
                        </div>
                    </div>
                    <div class="cell small-6 medium-4 xlarge-4">
                        <div class="card">
                            <div class="count-card">
                                <span class="count-number">{{ summary.completed }}</span>
                                <span class="count-name">Completed</span>
                            </div>
                        </div>
                    </div>
                    <div class="cell small-6 medium-4 xlarge-4">
                        <div class="card">
                            <div class="count-card">
                                <span class="count-number">{{ summary.failed }}</span>
                                <span class="count-name">Failed</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section>
            <div class="requisitions-table grid-x grid-padding-x grid-padding-y py-2">
                <div class="cell small-12">
                    <Pagination
                        :totalPages="totalPages"
                        :currentPage="currentPage"
                        :maxPagesToShow="10"
                        @page-changed="handlePageChange"
                    />

                    <table class="table">
                        <thead>
                            <tr>
                                <th>Requisition</th>
                                <th>Person Specification</th>
                                <th>Candidate Name</th>
                                <th>Verification Type</th>
                                <th>iFact Check</th>
                                <th>Status</th>
                                <th>Manual Pass/ Fail Override</th>
                                <th>Attachment</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="backgroundVerificationResult in backgroundVerificationResults"
                                :key="backgroundVerificationResult.id">
                                <td>
                                    {{ requisitionTitleTable(backgroundVerificationResult.job_application.job) }}
                                </td>
                                <td>
                                    {{ backgroundVerificationResult.background_verification_spec.person_specification.full_title }}
                                </td>
                                <td>
                                    {{ backgroundVerificationResult.job_application.user.full_name }}
                                </td>
                                <td>
                                    {{ (backgroundVerificationResult.background_verification_spec !== null) ? backgroundVerificationResult.background_verification_spec.title : '' }}
                                </td>
                                <td>
                                    {{ (backgroundVerificationResult.background_verification_spec.ifacts_check !== null)? backgroundVerificationResult.background_verification_spec.ifacts_check.title : '' }}
                                </td>
                                <td>
                                    {{ backgroundVerificationResult.result_text }}
                                    <span v-if="backgroundVerificationResult.result !== null"><br/><i>{{ backgroundVerificationResult.result }}</i></span>
                                </td>
                                <td>
                                    <select class="border-0"
                                        @change="updateResultStatus(backgroundVerificationResult.id, backgroundVerificationResult.result)"
                                        v-model="backgroundVerificationResult.result">
                                        <option></option>
                                        <option value="pass">Pass</option>
                                        <option value="fail">Fail</option>
                                    </select>
                                </td>
                                <td><a v-if="backgroundVerificationResult.result_file !== null" target="_blank" :href="backgroundVerificationResult.result_file_cdn">{{
                                        sliceString(backgroundVerificationResult.result_file) }}</a></td>
                            </tr>
                        </tbody>
                    </table>

                    <Pagination
                        :totalPages="totalPages"
                        :currentPage="currentPage"
                        :maxPagesToShow="10"
                        @page-changed="handlePageChange"
                    />
                </div>
            </div>
        </section>
    </div>
</template>